// SUB-OFFICE 7 — dynamic blob shadows: one InstancedMesh of radial-alpha
// discs for everything that MOVES (the player, the ghost petitioners).
// Static furniture grounds itself through shadows baked into the per-room
// floor canvases; this pool exists only so walking bodies stop floating.
// Deliberately NOT dim-patched: a shadow is the absence of light, and it
// does not brighten when the hall dims for a ceremony.

import {
  CanvasTexture, DynamicDrawUsage, InstancedMesh, Matrix4, MeshBasicMaterial,
  PlaneGeometry, SRGBColorSpace,
} from 'three';
import { mkCanvas } from './textures';
import { PAL, mix, rgba, roomShadow } from '../palette';
import type { Entity } from '../entities/entities';
import type { Player } from '../entities/player';
import type { Ghosts } from '../entities/ghosts';

const CAP = 16; // player + ghost cap (12) + margin

// v3-3 (§5.2): the moving-body grounding disc is nudged off pure neutral carbon
// toward the building's warm carbon-brown room shadow so the ghost crowd (and
// the player) feel planted in the room's temperature instead of floating on
// dead grey. One shared disc serves all bodies (roomMoodAt is private to
// world.ts and per-instance room lookup would overbuild for +0 payoff), so a
// single warm interior hue stands in for the crowd's dominant surroundings.
const SHADOW_TINT = mix(PAL.carbon, roomShadow('hall'), 0.5);

export class BlobShadows implements Entity {
  readonly id = 'shadows';
  readonly mesh: InstancedMesh;

  private mat4 = new Matrix4();
  private buf = new Float32Array((CAP - 1) * 2);

  constructor(private player: Player, private ghosts: Ghosts) {
    const [c, ctx] = mkCanvas(64, 64);
    ctx.clearRect(0, 0, 64, 64);
    const g = ctx.createRadialGradient(32, 32, 4, 32, 32, 30);
    g.addColorStop(0, rgba(SHADOW_TINT, 0.40));
    g.addColorStop(0.65, rgba(SHADOW_TINT, 0.16));
    g.addColorStop(1, rgba(SHADOW_TINT, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const tex = new CanvasTexture(c);
    tex.colorSpace = SRGBColorSpace;

    const geo = new PlaneGeometry(0.72, 0.56);
    geo.rotateX(-Math.PI / 2);
    const mat = new MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
    this.mesh = new InstancedMesh(geo, mat, CAP);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.frustumCulled = false; // the single-bounding-sphere bug, pre-empted
  }

  update(): void {
    let n = 0;
    this.mat4.makeTranslation(this.player.pos.x, 0.012, this.player.pos.z);
    this.mesh.setMatrixAt(n++, this.mat4);
    const pairs = this.ghosts.collectShadowXZ(this.buf);
    for (let i = 0; i < pairs && n < CAP; i++) {
      this.mat4.makeTranslation(this.buf[i * 2], 0.012, this.buf[i * 2 + 1]);
      this.mesh.setMatrixAt(n++, this.mat4);
    }
    for (let i = n; i < CAP; i++) {
      this.mat4.makeTranslation(0, -60, 0); // parked below the building
      this.mesh.setMatrixAt(i, this.mat4);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
